import React from 'react';
import { motion } from 'framer-motion';
import { LuCheck, LuHeart, LuInfo, LuPlus, LuStar } from 'react-icons/lu';
import { VectorGlyphs } from '../oracle/VectorGlyphs';
import ButtonSpinner from '../oracle/ButtonSpinner';
import ButtonShimmer from '../oracle/ButtonShimmer';
import { GLOWUP_VECTORS, HOLISTIC_TARGET, glowupVectorLabel } from '../../contracts/glowupApi';
import { COPY } from '../../contracts/copyMap';

// ExploreEncouragementRail — the EXPLORE surface's "LEAVE ENCOURAGEMENT"
// panel (design-recreation desktop right rail, 340px / rgba(38,38,37,0.55)).
//
// One DOM instance serves both breakpoints:
//   desktop (lg+): caps title + info icon, stacked vector heart rows.
//   mobile (<lg):  sentence-case "Encourage their glowup" title, compact
//                  2-column heart grid (descriptions hidden). Replaces the
//                  mockup's display-only rating chips with the REAL vector
//                  encouragement interaction.
//
// Send flow (controlled by the page):
//   selected.length === 0      -> disabled #242423 button
//   selected.length >= 1       -> metallic shimmer button
//   confirmation === true      -> "ENCOURAGEMENT SENT" + check, then the page
//                                 advances and resets hearts (~1.2s).
//
// Vector targets come from the glowup contract: the 6 GLOWUP_VECTORS plus
// the optional holistic "biggest_overall" row.

const DESCRIPTIONS = Object.freeze({
  skin_quality: COPY.ENCOURAGE_VECTOR_SKIN_QUALITY_DESC,
  hair_scalp: COPY.ENCOURAGE_VECTOR_HAIR_SCALP_DESC,
  facial_hair: COPY.ENCOURAGE_VECTOR_FACIAL_HAIR_DESC,
  brows_lashes: COPY.ENCOURAGE_VECTOR_BROWS_LASHES_DESC,
  mouth_smile: COPY.ENCOURAGE_VECTOR_MOUTH_SMILE_DESC,
  definition: COPY.ENCOURAGE_VECTOR_DEFINITION_DESC,
  [HOLISTIC_TARGET]: COPY.ENCOURAGE_VECTOR_HOLISTIC_DESC,
});


const VectorHeartRow = ({ target, active, onToggle }) => {
  const isHolistic = target === HOLISTIC_TARGET;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={[
        'flex items-center gap-3 w-full text-left px-3.5 py-[11px] rounded-field border',
        'font-display transition-all duration-200 ease-mogged cursor-pointer',
        active
          ? 'bg-aurum/10 border-aurum/55'
          : 'bg-[rgba(24,24,23,0.7)] border-hairline-soft hover:border-hairline',
      ].join(' ')}
    >
      <span
        className={['inline-flex items-center shrink-0', active ? 'text-aurum' : 'text-bone-dim'].join(' ')}
        aria-hidden="true"
      >
        {isHolistic ? <LuStar size={16} /> : VectorGlyphs[target]}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className="block uppercase tracking-[0.16em] text-[#E8E2D6] truncate"
          style={{ fontSize: 12 }}
        >
          {glowupVectorLabel(target)}
        </span>
        <span
          className="hidden lg:block text-bone-dim mt-px truncate"
          style={{ fontSize: 14 }}
        >
          {DESCRIPTIONS[target] || ''}
        </span>
      </span>
      <motion.span
        className="inline-flex shrink-0"
        animate={active ? { scale: [1, 1.25, 1] } : { scale: 1 }}
        transition={{ duration: 0.28, ease: [0.65, 0, 0.35, 1] }}
        aria-hidden="true"
      >
        <LuHeart
          size={20}
          className={active ? 'text-aurum' : 'text-bone-dim'}
          fill={active ? 'currentColor' : 'none'}
          style={active ? { filter: 'drop-shadow(0 0 6px rgba(201,164,92,0.6))' } : undefined}
        />
      </motion.span>
    </button>
  );
};

const ExploreEncouragementRail = ({
  selected = [],
  onToggle,
  onSubmit,
  onSkip,
  submitting = false,
  confirmation = false,
  includeHolistic = true,
  footnote = null,
  className = '',
}) => {
  const selectedSet = new Set(selected);
  const targets = includeHolistic ? [...GLOWUP_VECTORS, HOLISTIC_TARGET] : GLOWUP_VECTORS;
  const canSubmit = !submitting && !confirmation && selected.length > 0;

  return (
    <aside
      className={[
        'w-full lg:w-[340px] lg:shrink-0',
        'bg-[rgba(38,38,37,0.55)] border border-white/10 rounded-card p-[18px] lg:p-[22px]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="leave encouragement"
    >
      {/* desktop header */}
      <div className="hidden lg:flex justify-between items-center">
        <h2
          className="font-display uppercase tracking-[0.2em] text-bone m-0"
          style={{ fontSize: 13 }}
        >
          {COPY.ENCOURAGE_HEADER}
        </h2>
        <LuInfo size={16} className="text-bone-dim" aria-hidden="true" />
      </div>
      {/* mobile header */}
      <h2 className="lg:hidden font-display text-bone m-0" style={{ fontSize: 23 }}>
        Encourage their glowup
      </h2>
      <p
        className="font-display text-bone-dim mt-2.5 mb-4"
        style={{ fontSize: 14.5, lineHeight: 1.5 }}
      >
        {COPY.ENCOURAGE_HELPER}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 lg:gap-[9px]">
        {targets.map((target) => (
          <VectorHeartRow
            key={target}
            target={target}
            active={selectedSet.has(target)}
            onToggle={() => onToggle && onToggle(target)}
          />
        ))}
      </div>

      {/* Submitting keeps the live metallic fill and adds the shimmer sweep.
          It must not fall through to the disabled #242423 branch — that greys
          the button at the exact moment the user is waiting to hear whether
          their press landed. */}
      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSubmit}
        aria-busy={submitting || undefined}
        className={[
          'relative overflow-hidden w-full h-[52px] mt-4 rounded-field border-0',
          'flex items-center justify-center gap-2.5',
          'font-display font-semibold uppercase tracking-[0.22em]',
          'transition-all duration-200 ease-mogged select-none',
          confirmation
            ? 'bg-[#242423] text-bone-dim cursor-default'
            : submitting
              ? 'bg-metallic bg-[length:200%_100%] text-obsidian cursor-wait shadow-card-lift'
              : canSubmit
                ? 'bg-metallic bg-[length:200%_100%] motion-safe:animate-shimmer text-obsidian cursor-pointer shadow-card-lift hover:shadow-aurum-glow-strong'
                : 'bg-[#242423] text-[#6E6A62] cursor-not-allowed',
        ].join(' ')}
        style={{ fontSize: 13.5 }}
      >
        {/* bg-metallic while submitting — the strong band, or it vanishes. */}
        {submitting && <ButtonShimmer onLight />}
        <span className="relative z-10">
          {confirmation
            ? COPY.ENCOURAGE_SENT_CONFIRM
            : submitting
              ? COPY.BUTTON_SUBMITTING
              : COPY.ENCOURAGE_SEND_CTA}
        </span>
        {confirmation && <LuCheck size={15} aria-hidden="true" className="relative z-10" />}
        {!confirmation && !submitting && <LuPlus size={15} aria-hidden="true" className="relative z-10" />}
        {submitting && <ButtonSpinner className="relative z-10" />}
      </button>

      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          disabled={submitting || confirmation}
          className={[
            'w-full mt-[11px] py-[13px] bg-transparent border border-white/10 rounded-field',
            'font-display uppercase tracking-[0.22em] text-bone-dim',
            'transition-colors duration-200 ease-mogged cursor-pointer',
            'hover:text-[#D8D2C6] hover:border-hairline',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          ].join(' ')}
          style={{ fontSize: 12 }}
        >
          {COPY.ENCOURAGE_SKIP_CTA} →
        </button>
      )}

      {footnote}
    </aside>
  );
};

export default ExploreEncouragementRail;
